// Normalized payment errors sent by pj-orders-rails (`payment_error`).
// The frontend never shows gateway messages: only the normalized message, or a local fallback.
// Kept free of imports so it can be tested with `node --test`.

export const PAYMENT_TRANSACTION_STATES = {
  declined: 'declined',
  failed: 'failed',
  pendingVerification: 'pending_verification'
};

const REASON_FORMAT = /^[A-Z_]+$/;

const pick = (object, snakeKey, camelKey) => object?.[snakeKey] ?? object?.[camelKey];

// Error bodies send `request_id` next to `payment_error`; the order serializer sends it inside.
const findPaymentError = source => {
  const data = source && typeof source === 'object' && 'ok' in source ? source.data : source;
  const errors = Array.isArray(data?.errors) ? data.errors[0] : data?.errors;
  const container = [data, errors].find(candidate => pick(candidate, 'payment_error', 'paymentError'));
  const paymentError = pick(container, 'payment_error', 'paymentError');

  return {
    paymentError,
    requestId: pick(paymentError, 'request_id', 'requestId') || pick(container, 'request_id', 'requestId')
  };
};

const isValidPaymentError = paymentError =>
  REASON_FORMAT.test(paymentError?.reason || '') &&
  typeof paymentError.message === 'string' &&
  paymentError.message.trim() !== '';

/**
 * Returns the normalized payment error of an API response (apisauce), an error body or an order.
 * @returns {{reason, message, category, transactionState, retryable, action, requestId} | null}
 */
export const getPaymentError = source => {
  const { paymentError, requestId } = findPaymentError(source);
  if (!isValidPaymentError(paymentError)) {
    return null;
  }

  return {
    reason: paymentError.reason,
    message: paymentError.message,
    category: paymentError.category,
    transactionState: pick(paymentError, 'transaction_state', 'transactionState'),
    retryable: paymentError.retryable === true,
    action: paymentError.action,
    requestId: requestId || null
  };
};

// Local error for failures whose result is unknown (e.g. the order could not be read).
export const getPendingVerificationError = message => ({
  reason: 'PAYMENT_STATUS_UNKNOWN',
  message,
  category: 'unknown',
  transactionState: PAYMENT_TRANSACTION_STATES.pendingVerification,
  retryable: false,
  action: 'DO_NOT_RETRY_CONTACT_SUPPORT',
  requestId: null
});

export const isPaymentPendingVerification = paymentError =>
  paymentError?.transactionState === PAYMENT_TRANSACTION_STATES.pendingVerification;

// Only an explicit retryable error allows a new attempt: without one the result is unknown.
export const isPaymentRetryAllowed = paymentError =>
  paymentError?.retryable === true && !isPaymentPendingVerification(paymentError);
